import * as React from "react";
import { Search, Car } from "lucide-react";
import { DateRangeInput } from "./DateRangeInput";
import { GuestsSelector } from "./GuestsSelector";
import {
  VehicleCategorySelector,
  type VehicleCategorySelection,
} from "./VehicleCategorySelector";
import { Button } from "./Button";
import { cn } from "../../lib/utils";
import type { CategoryWithDetails } from "@fm/api";
import { add } from "date-fns";

export interface SearchBarFormData {
  pickUp: string;
  return: string;
  adults: number;
  children: number;
  infants: number;
  pets: number;
  categoryId?: string;
  vehicleId?: string;
}

export interface SearchBarProps {
  formData: SearchBarFormData;
  categoriesList: CategoryWithDetails[];
  vehicleCategorySelection: VehicleCategorySelection | null;
  unavailableDates: Set<string>;
  isLoading: boolean;
  errors: Partial<SearchBarFormData>;
  isMobile: boolean;
  theme?: "light" | "dark";
  layout?: "auto" | "horizontal" | "vertical";
  placeholder?: string;
  primaryColor?: string;
  onSearch?: (data: SearchBarFormData) => void;
  onChange?: (data: SearchBarFormData) => void;
  onVehicleCategoryChange?: (
    selection: VehicleCategorySelection | null,
  ) => void;
  onDateRangeChange?: (range: [string, string] | null) => void;
  showVehicleSelector?: boolean;
  className?: string;
  disabled?: boolean;
  uncategorizedVehicles?: Array<{
    id: string;
    name: string;
    make?: string;
    model?: string;
  }>;
}

export function SearchBar({
  formData,
  categoriesList,
  vehicleCategorySelection,
  unavailableDates,
  isLoading,
  errors,
  isMobile,
  theme = "light",
  layout = "auto",
  placeholder = "Search vehicles",
  primaryColor,
  onSearch,
  onChange,
  onVehicleCategoryChange,
  onDateRangeChange,
  showVehicleSelector = true,
  className,
  disabled = false,
  uncategorizedVehicles = [],
}: SearchBarProps) {
  // Date range state for DateRangeInput
  const [dateRange, setDateRange] = React.useState<[string, string] | null>(
    null,
  );

  // Helper function to darken a hex color for hover state
  const darkenColor = (hex: string, percent: number = 10): string => {
    const num = parseInt(hex.replace("#", ""), 16);
    const r = Math.max(0, (num >> 16) - Math.round(2.55 * percent));
    const g = Math.max(0, ((num >> 8) & 0x00ff) - Math.round(2.55 * percent));
    const b = Math.max(0, (num & 0x0000ff) - Math.round(2.55 * percent));
    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
  };

  // Button styles with custom primary color support
  const buttonStyle = primaryColor
    ? ({
        "--fm-primary": primaryColor,
        "--fm-primary-hover": darkenColor(primaryColor, 10),
        backgroundColor: primaryColor,
      } as React.CSSProperties)
    : undefined;

  // Sync dateRange with formData
  React.useEffect(() => {
    if (formData.pickUp && formData.return) {
      setDateRange([formData.pickUp, formData.return]);
    } else {
      setDateRange(null);
    }
  }, [formData.pickUp, formData.return]);

  const updateFormData = (updates: Partial<SearchBarFormData>) => {
    const newFormData = { ...formData, ...updates };
    onChange?.(newFormData);
  };

  const handleVehicleCategoryChange = (
    selection: VehicleCategorySelection | null,
  ) => {
    onVehicleCategoryChange?.(selection);
  };

  // Date range handler
  const handleDateRangeChange = (range: [string, string] | null) => {
    setDateRange(range);
    onDateRangeChange?.(range);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    onSearch?.(formData);
  };

  // Determine layout based on props and screen size
  const isHorizontal = (() => {
    if (layout === "horizontal") return true;
    if (layout === "vertical") return false;
    if (layout === "auto") {
      // Auto mode: horizontal on desktop, vertical on mobile
      return !isMobile;
    }
    return false;
  })();

  if (isHorizontal) {
    return (
      <div className={cn("w-full", className)}>
        <form onSubmit={handleSubmit}>
          {/* Premium Search Bar - Airbnb/Booking.com Style */}
          <div
            className={cn(
              "flex items-center bg-white rounded-xl shadow-lg border border-gray-200 transition-all duration-200 hover:shadow-xl group",
              theme === "dark" && "bg-gray-800 border-gray-700",
            )}
          >
            {/* Vehicle Type Field */}
            {showVehicleSelector && (
              <div className="flex-1 min-w-0 relative">
                <div className="flex items-center px-6 py-4 transition-colors duration-200 cursor-pointer">
                  <Car className="w-5 h-5 text-gray-600 mr-3 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <VehicleCategorySelector
                      showLabel={false}
                      categories={categoriesList}
                      value={vehicleCategorySelection}
                      onChange={handleVehicleCategoryChange}
                      placeholder="Vehicle Type"
                      disabled={disabled}
                      uncategorizedVehicles={uncategorizedVehicles}
                      className="w-full bg-transparent border-0 p-0 text-gray-900 placeholder:text-gray-500 focus:ring-0 focus:outline-none text-sm font-medium"
                    />
                  </div>
                </div>
                {/* Divider */}
                <div className="absolute right-0 top-1/2 transform -translate-y-1/2 w-px h-8 bg-gray-200" />
              </div>
            )}

            {/* Date Range Field */}
            <div className="flex-1 min-w-0 relative">
              <div className="flex items-center px-6 py-4 transition-colors duration-200 cursor-pointer">
                <svg
                  className="w-5 h-5 text-gray-600 mr-3 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <div className="flex-1 min-w-0">
                  <DateRangeInput
                    value={dateRange}
                    onChange={handleDateRangeChange}
                    placeholder="Select dates"
                    theme="light"
                    primaryColor={primaryColor}
                    error={errors.pickUp || errors.return}
                    unavailableDates={unavailableDates}
                    showLabels={false}
                    showToday={true}
                    minDate={add(new Date(), { days: -1 })}
                    disabled={disabled}
                    className="w-full bg-transparent border-0 p-0 text-gray-900 placeholder:text-gray-500 focus:ring-0 focus:outline-none text-sm font-medium"
                  />
                </div>
              </div>
              {/* Divider */}
              <div className="absolute right-0 top-1/2 transform -translate-y-1/2 w-px h-8 bg-gray-200" />
            </div>

            {/* Guests Field */}
            <div className="flex-1 min-w-0 relative">
              <div className="flex items-center px-6 py-4 transition-colors duration-200 cursor-pointer">
                <svg
                  className="w-5 h-5 text-gray-600 mr-3 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
                <div className="flex-1 min-w-0 border border-gray-200 rounded-md h-10 px-3">
                  <GuestsSelector
                    adults={formData.adults}
                    childrenCount={formData.children}
                    infants={formData.infants}
                    pets={formData.pets}
                    onGuestsChange={(guests) => {
                      updateFormData(guests);
                    }}
                    disabled={disabled}
                    className="w-full bg-transparent border-0 p-0 text-gray-900 placeholder:text-gray-500 focus:ring-0 focus:outline-none text-sm font-medium"
                  />
                </div>
              </div>
            </div>

            {/* Search Button */}
            <div className="p-2 mr-3">
              <Button
                type="submit"
                disabled={isLoading || disabled}
                className={cn(
                  "h-12 px-8 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed focus:ring-2 focus:ring-offset-2 flex items-center justify-center",
                  !primaryColor &&
                    "bg-blue-600 hover:bg-blue-700 focus:ring-blue-500",
                )}
                style={buttonStyle}
                onMouseEnter={(e) => {
                  if (primaryColor) {
                    e.currentTarget.style.backgroundColor = darkenColor(
                      primaryColor,
                      10,
                    );
                  }
                }}
                onMouseLeave={(e) => {
                  if (primaryColor) {
                    e.currentTarget.style.backgroundColor = primaryColor;
                  }
                }}
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                    <span className="text-sm">Searching...</span>
                  </>
                ) : (
                  <>
                    <Search className="h-4 w-4 mr-2" />
                    <span className="text-sm font-semibold">Search</span>
                  </>
                )}
              </Button>
            </div>
          </div>
        </form>
      </div>
    );
  }

  // Premium Vertical Layout
  return (
    <div className={cn("w-full max-w-lg mx-auto", className)}>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Header */}
        <div className="text-center">
          <h2
            className={cn(
              "text-2xl font-bold mb-3 flex items-center justify-center",
              theme === "dark" ? "text-white" : "text-gray-900",
            )}
          >
            <Car
              className="h-6 w-6 mr-3"
              style={primaryColor ? { color: primaryColor } : undefined}
            />
            {placeholder}
          </h2>
          <p
            className={cn(
              "text-sm font-medium",
              theme === "dark" ? "text-gray-400" : "text-gray-600",
            )}
          >
            Find your perfect vehicle for your next adventure
          </p>
        </div>

        {/* Search Container */}
        <div
          className={cn(
            "bg-white rounded-xl shadow-lg border border-gray-200 transition-all duration-200 hover:shadow-xl",
            theme === "dark" && "bg-gray-800 border-gray-700",
          )}
        >
          <div className="p-6 space-y-4">
            {/* Vehicle/Category Selector */}
            {showVehicleSelector && (
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Vehicle Type (Optional)
                </label>
                <div className="flex items-center px-6 py-4 transition-colors duration-200 cursor-pointer">
                  <Car className="w-5 h-5 text-gray-600 mr-3 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <VehicleCategorySelector
                      categories={categoriesList}
                      value={vehicleCategorySelection}
                      onChange={handleVehicleCategoryChange}
                      placeholder="Any vehicle type"
                      disabled={disabled}
                      uncategorizedVehicles={uncategorizedVehicles}
                      className="w-full bg-transparent border-0 p-0 text-gray-900 placeholder:text-gray-500 focus:ring-0 focus:outline-none text-sm font-medium"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Date Range */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                Select dates
              </label>
              <div className="flex items-center px-6 py-4 transition-colors duration-200 cursor-pointer">
                <svg
                  className="w-5 h-5 text-gray-600 mr-3 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <div className="flex-1 min-w-0">
                  <DateRangeInput
                    value={dateRange}
                    onChange={handleDateRangeChange}
                    placeholder="Select dates"
                    theme={theme === "dark" ? "dark" : "light"}
                    primaryColor={primaryColor}
                    error={errors.pickUp || errors.return}
                    unavailableDates={unavailableDates}
                    showLabels={false}
                    showToday={true}
                    minDate={add(new Date(), { days: -1 })}
                    disabled={disabled}
                    className="w-full bg-transparent border-0 p-0 text-gray-900 placeholder:text-gray-500 focus:ring-0 focus:outline-none text-sm font-medium"
                  />
                </div>
              </div>
            </div>

            {/* Guests */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                Guests
              </label>
              <div className="flex items-center px-6 py-4 transition-colors duration-200 cursor-pointer">
                <svg
                  className="w-5 h-5 text-gray-600 mr-3 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
                <div className="flex-1 min-w-0 border border-gray-200 rounded-md h-10 px-3">
                  <GuestsSelector
                    adults={formData.adults}
                    childrenCount={formData.children}
                    infants={formData.infants}
                    pets={formData.pets}
                    onGuestsChange={(guests) => {
                      updateFormData(guests);
                    }}
                    disabled={disabled}
                    className="w-full bg-transparent border-0 p-0 text-gray-900 placeholder:text-gray-500 focus:ring-0 focus:outline-none text-sm font-medium"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Search Button */}
        <Button
          type="submit"
          disabled={isLoading || disabled}
          className={cn(
            "w-full h-12 text-base font-semibold text-white shadow-md hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg focus:ring-2 focus:ring-offset-2 flex items-center justify-center",
            !primaryColor &&
              "bg-blue-600 hover:bg-blue-700 focus:ring-blue-500",
          )}
          style={buttonStyle}
          onMouseEnter={(e) => {
            if (primaryColor) {
              e.currentTarget.style.backgroundColor = darkenColor(
                primaryColor,
                10,
              );
            }
          }}
          onMouseLeave={(e) => {
            if (primaryColor) {
              e.currentTarget.style.backgroundColor = primaryColor;
            }
          }}
        >
          {isLoading ? (
            <>
              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
              <span>Searching...</span>
            </>
          ) : (
            <>
              <Search className="h-4 w-4 mr-2" />
              <span>Search Vehicles</span>
            </>
          )}
        </Button>
      </form>
    </div>
  );
}
